import classNames from "classnames";
import React, { useState } from "react";
import { format, parseISO } from "date-fns";
import { CheckIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { AvatarGroup } from "@design-system/Avatar/AvatarGroup";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { AvatarTooltip } from "@components/avatar/AvatarTooltip";
import { getReviewableFields } from "@hooks/useReviewableFields";
import styles from "./TransactionDetailsReview.module.scss";

/** A single reviewer entry for a reviewed field. */
export interface TransactionFieldReviewer {
    userId: number;
    name: string;
    reviewedAt: string;
    avatarUrl: string | null;
    iconColor: string;
    email: string | null;
}

/** Per-field review state for a transaction. */
export interface TransactionFieldReviewState {
    counterparty: TransactionFieldReviewer[];
    category: TransactionFieldReviewer[];
    class: TransactionFieldReviewer[];
    entity: TransactionFieldReviewer[];
    cashLedger: TransactionFieldReviewer[];
    accrualLedger: TransactionFieldReviewer[];
    note: TransactionFieldReviewer[];
}

interface ReviewRowProps {
    label: string;
    reviewers: TransactionFieldReviewer[];
    currentUserId: number;
    onMarkReviewed?: (field: TransactionReviewField) => void;
    onUndoReview?: (field: TransactionReviewField) => void;
    fieldKey: TransactionReviewField;
    /**
     * Whether this field's current value can be marked as reviewed (e.g.
     * false for an uncategorized category or a blank/uncategorized GL
     * account). Defaults to true. Ineligible fields hide the "Mark reviewed"
     * action entirely rather than disabling it, mirroring the backend, which
     * silently drops ineligible fields instead of erroring.
     */
    isEligible?: boolean;
}

const REVIEWER_DATE_FORMAT = "MMM d, yyyy, h:mmaaa";

const ReviewerAvatars: React.FC<{ reviewers: TransactionFieldReviewer[] }> = ({
    reviewers,
}) =>
    reviewers.length > 0 && (
        <AvatarGroup
            items={reviewers}
            getKey={(reviewer) => `${reviewer.userId}-${reviewer.reviewedAt}`}
            getLabel={(reviewer) => reviewer.name}
            renderAvatar={(reviewer) => (
                <AvatarTooltip
                    title={reviewer.name}
                    content={format(
                        parseISO(reviewer.reviewedAt),
                        REVIEWER_DATE_FORMAT,
                    )}
                >
                    <UserAvatar
                        user={{
                            name: reviewer.name,
                            avatar: reviewer.avatarUrl,
                            iconColor: reviewer.iconColor,
                            email: reviewer.email,
                        }}
                    />
                </AvatarTooltip>
            )}
            max={reviewers.length}
            size="sm"
        />
    );

const ReviewRow: React.FC<ReviewRowProps> = ({
    label,
    reviewers,
    currentUserId,
    onMarkReviewed,
    onUndoReview,
    fieldKey,
    isEligible = true,
}) => {
    const [isHighlighted, setIsHighlighted] = useState(false);
    const isReviewedByMe = reviewers.some((r) => r.userId === currentUserId);

    const markReviewedClassName = classNames(styles.markReviewedBtn, {
        [styles.markReviewedBtnMuted]: !isHighlighted,
    });

    return (
        <div
            className={styles.reviewRow}
            onMouseEnter={() => setIsHighlighted(true)}
            onMouseLeave={() => setIsHighlighted(false)}
            onFocus={() => setIsHighlighted(true)}
            onBlur={() => setIsHighlighted(false)}
        >
            <span className={styles.reviewLabel}>{label}</span>
            <div className={styles.reviewStatus}>
                {isReviewedByMe && isHighlighted && (
                    <Button
                        variant="primary"
                        size="sm"
                        className={styles.undoReviewBtn}
                        onClick={() => onUndoReview?.(fieldKey)}
                    >
                        Undo review
                    </Button>
                )}
                {isReviewedByMe && !isHighlighted && (
                    <span className={styles.reviewed}>
                        <CheckIcon className={styles.reviewedIcon} />
                        Reviewed
                    </span>
                )}
                {!isReviewedByMe && isEligible && (
                    <Button
                        {...(isHighlighted
                            ? { variant: "primary", size: "sm" }
                            : { variant: "link", size: "sm" })}
                        className={markReviewedClassName}
                        onClick={() => onMarkReviewed?.(fieldKey)}
                    >
                        Mark reviewed
                    </Button>
                )}
            </div>
            <ReviewerAvatars reviewers={reviewers} />
        </div>
    );
};

interface Props {
    reviewState: TransactionFieldReviewState;
    currentUserId: number;
    onMarkReviewed?: (field: TransactionReviewField) => void;
    onUndoReview?: (field: TransactionReviewField) => void;
    hasAccrualLedger?: boolean;
    isGlFirst?: boolean;
    /** False when the category is the uncategorized placeholder. */
    isCategoryEligible?: boolean;
    /** False when the cash ledger is blank or an uncategorized placeholder account. */
    isCashLedgerEligible?: boolean;
    /** False when the accrual ledger is blank or an uncategorized placeholder account. */
    isAccrualLedgerEligible?: boolean;
}

export const TransactionDetailsReview: React.FC<Props> = ({
    reviewState,
    currentUserId,
    onMarkReviewed,
    onUndoReview,
    hasAccrualLedger,
    isGlFirst,
    isCategoryEligible = true,
    isCashLedgerEligible = true,
    isAccrualLedgerEligible = true,
}) => {
    const reviewableFields = getReviewableFields({
        isGlFirst: isGlFirst ?? false,
        hasAccrualLedger: hasAccrualLedger ?? false,
    });

    return (
        <div className={styles.container}>
            {reviewableFields.includes(TransactionReviewField.CATEGORY) && (
                <ReviewRow
                    label="Category"
                    reviewers={reviewState.category}
                    currentUserId={currentUserId}
                    onMarkReviewed={onMarkReviewed}
                    onUndoReview={onUndoReview}
                    fieldKey={TransactionReviewField.CATEGORY}
                    isEligible={isCategoryEligible}
                />
            )}
            <ReviewRow
                label="Counterparty"
                reviewers={reviewState.counterparty}
                currentUserId={currentUserId}
                onMarkReviewed={onMarkReviewed}
                onUndoReview={onUndoReview}
                fieldKey={TransactionReviewField.COUNTERPARTY}
            />
            <ReviewRow
                label="Class"
                reviewers={reviewState.class}
                currentUserId={currentUserId}
                onMarkReviewed={onMarkReviewed}
                onUndoReview={onUndoReview}
                fieldKey={TransactionReviewField.CLASS}
            />
            <ReviewRow
                label="Cash ledger"
                reviewers={reviewState.cashLedger}
                currentUserId={currentUserId}
                onMarkReviewed={onMarkReviewed}
                onUndoReview={onUndoReview}
                fieldKey={TransactionReviewField.CASH_LEDGER}
                isEligible={isCashLedgerEligible}
            />
            {reviewableFields.includes(
                TransactionReviewField.ACCRUAL_LEDGER,
            ) && (
                <ReviewRow
                    label="Accrual ledger"
                    reviewers={reviewState.accrualLedger}
                    currentUserId={currentUserId}
                    onMarkReviewed={onMarkReviewed}
                    onUndoReview={onUndoReview}
                    fieldKey={TransactionReviewField.ACCRUAL_LEDGER}
                    isEligible={isAccrualLedgerEligible}
                />
            )}
            <ReviewRow
                label="Entity"
                reviewers={reviewState.entity}
                currentUserId={currentUserId}
                onMarkReviewed={onMarkReviewed}
                onUndoReview={onUndoReview}
                fieldKey={TransactionReviewField.ENTITY}
            />
            <ReviewRow
                label="Memo"
                reviewers={reviewState.note}
                currentUserId={currentUserId}
                onMarkReviewed={onMarkReviewed}
                onUndoReview={onUndoReview}
                fieldKey={TransactionReviewField.NOTE}
            />
        </div>
    );
};
